import { customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ENCRYPTION_EXCLUSIONS_GRID } from '../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import { Column, ColumnKeys, getGridColumnDetails, GRID_COLUMNS } from './grid-config';
import { GzEncryptionExclusionsGridDataProvider } from './GzEncryptionExclusionsGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { MAX_ENTITY_LENGTH, validateDuplicates, validateEntityByType } from '../utils/validators';
import { GRID_ITEM_FORM_ID } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { GridCellEditWebComponent } from '../../../../../../../grid/uiElements/gridCellEdit';
import { EncryptionExclusionItem, EncryptionExclusionItemType } from '@nirvana/shared/dtos/types/policies/common';
import { CustomizableValidator } from '../../../../../../../ui/gzForm/validators/customizable.validator';
import { MaxLengthValidator } from '../../../../../../../ui/gzForm/validators/max-length.validator';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { toggleVisibility } from '../../../../../../../utils/htmlElement.utils';

@customElement(WEB_COMPONENT_POLICY_PANEL_ENCRYPTION_EXCLUSIONS_GRID)
export class GzEncryptionExclusionsGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  EncryptionExclusionItem,
  EncryptionExclusionItem,
  GzEncryptionExclusionsGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected translations = {
    editRow: translateFn('Edit exclusion'),
    deleteRow: translateFn('Delete'),
  };

  protected getDataProvider(): GzEncryptionExclusionsGridDataProvider {
    return new GzEncryptionExclusionsGridDataProvider(this.initialGridItems);
  }

  protected renderGridItemForm(): TemplateResult {
    return html` <gz-policy-panel-encryption-exclusion-item
      id="${GRID_ITEM_FORM_ID}"
    ></gz-policy-panel-encryption-exclusion-item>`;
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: EncryptionExclusionItem): ValidationResult {
    // check for duplicates
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzEncryptionExclusionsGridDataProvider).items
    );
  }

  protected onEditRowEnabled(event: CustomEvent) {
    // set validators on edited row
    const { rowData } = event.detail;

    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.ENTITY, [
      RequiredValidator(translateFn('Required field')),
      MaxLengthValidator(
        MAX_ENTITY_LENGTH,
        translateFn('Maximum length permitted is {maxLength} characters', { maxLength: MAX_ENTITY_LENGTH })
      ),
      CustomizableValidator(value => validateEntityByType(rowData.item.type as EncryptionExclusionItemType, value)),
    ]);
  }

  protected isEditedRowChanged(oldItem: EncryptionExclusionItem, newItem: EncryptionExclusionItem): boolean {
    return oldItem.entity !== newItem.entity;
  }

  protected validateGridItemBeforeEdit(
    oldItem: EncryptionExclusionItem,
    newItem: EncryptionExclusionItem
  ): ValidationResult {
    // skip if entity is not changed
    if (newItem.entity === oldItem.entity) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected revertGridCellEditValue(gridCellEdit: GridCellEditWebComponent, oldItem: EncryptionExclusionItem): void {
    if (gridCellEdit.id === ColumnKeys.ENTITY) {
      gridCellEdit.value = oldItem.entity;
    }
  }

  protected onGridItemsChange(): void {
    super.onGridItemsChange();
    toggleVisibility(
      this.gridComponent,
      !!(this.gridComponent.dataProvider as GzEncryptionExclusionsGridDataProvider).items.length
    );
  }
}
